import { ElementType, forwardRef, ReactNode, UIEvent, useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'

import { useTracking } from '../services/context/TrackingContext'

const TrackScrollDepth = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode
    tag?: ElementType
    thresholds: number[]
    thresholdPropertyName: string
    event: string
    properties?: Record<string, unknown>
    className?: string
    useScrollXMediaQuery?: string
  }
>(
  (
    {
      children,
      tag: Wrapper = 'div',
      thresholds,
      thresholdPropertyName,
      event,
      properties,
      className,
      useScrollXMediaQuery = '(max-width: 0)', // defaults to vertical scrolling across the board
    },
    ref
  ) => {
    const { track } = useTracking()
    const [trackedThresholds, setTrackedThresholds] = useState<number[]>([])
    const useScrollX = useMediaQuery(useScrollXMediaQuery)

    const handleScroll = (e: UIEvent<HTMLElement>) => {
      const scrollDepth = useScrollX
        ? Math.ceil(e.currentTarget.scrollLeft) /
          Math.ceil(e.currentTarget.scrollWidth - e.currentTarget.clientWidth)
        : Math.ceil(e.currentTarget.scrollTop) /
          Math.ceil(e.currentTarget.scrollHeight - e.currentTarget.clientHeight)

      thresholds
        .filter((threshold) => !trackedThresholds.includes(threshold))
        .forEach((threshold) => {
          if (scrollDepth >= threshold) {
            track({
              event,
              properties: {
                ...(properties ?? {}),
                [thresholdPropertyName]: threshold * 100,
              },
            })
            setTrackedThresholds((prevState) => [...prevState, threshold])
          }
        })
    }

    return (
      <Wrapper
        ref={ref}
        className={className}
        onScroll={
          thresholds.length > trackedThresholds.length
            ? handleScroll
            : undefined
        }
      >
        {children}
      </Wrapper>
    )
  }
)

TrackScrollDepth.displayName = 'TrackScrollDepth'

export default TrackScrollDepth
